import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import MagazinePageHeader from './MagazinePageHeader';
import MagazineNewButton from './MagazineNewButton';
import MagazineFormModal from './MagazineFormModal';
import { useAuth } from '../../hooks/useAuth';
import {
  createIssue,
  deleteIssue,
  duplicateIssue,
  getIssues,
  getPublications,
  updateIssue,
} from '../../api/magazineService';

const emptyForm = { publication_id: '', title: '', issue_date: '', status: 'active' };
const emptyDuplicateForm = { ...emptyForm, copy_inventory: true };

const Issues = () => {
  const { canManageMagazineCatalog, canViewMagazineReports } = useAuth();
  const [publications, setPublications] = useState<any[]>([]);
  const [filterPublicationId, setFilterPublicationId] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [duplicateSource, setDuplicateSource] = useState<any | null>(null);
  const [duplicateForm, setDuplicateForm] = useState(emptyDuplicateForm);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadPublications = async () => {
    const data = await getPublications();
    setPublications(data);
  };

  const loadIssues = async (publicationId?: string) => {
    setLoading(true);
    const data = await getIssues(publicationId);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    loadPublications();
    loadIssues();
  }, []);

  useEffect(() => {
    loadIssues(filterPublicationId || undefined);
  }, [filterPublicationId]);

  const closeForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const openNewForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageMagazineCatalog) return;
    const payload = {
      ...form,
      publication_id: Number(form.publication_id),
    };
    if (editingId) {
      await updateIssue(editingId, payload);
    } else {
      await createIssue(payload);
    }
    closeForm();
    loadIssues(filterPublicationId || undefined);
  };

  const handleEdit = (item: any) => {
    setEditingId(item.id);
    setForm({
      publication_id: String(item.publication_id),
      title: item.title,
      issue_date: item.issue_date || '',
      status: item.status || 'active',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!canManageMagazineCatalog || !window.confirm('Delete this issue?')) return;
    await deleteIssue(id);
    loadIssues(filterPublicationId || undefined);
  };

  const openDuplicateForm = (item: any) => {
    setDuplicateSource(item);
    setDuplicateForm({
      publication_id: String(item.publication_id),
      title: `${item.title} (Copy)`,
      issue_date: '',
      status: 'active',
      copy_inventory: true,
    });
    setDuplicateError(null);
    setSuccessMessage(null);
  };

  const closeDuplicateForm = () => {
    if (duplicating) return;
    setDuplicateSource(null);
    setDuplicateForm(emptyDuplicateForm);
    setDuplicateError(null);
  };

  const handleDuplicate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageMagazineCatalog || !duplicateSource) return;
    setDuplicating(true);
    setDuplicateError(null);
    try {
      const result = await duplicateIssue(duplicateSource.id, {
        publication_id: Number(duplicateForm.publication_id),
        title: duplicateForm.title.trim(),
        issue_date: duplicateForm.issue_date || null,
        status: duplicateForm.status,
        copy_inventory: duplicateForm.copy_inventory,
      });
      setSuccessMessage(
        `Created ${result.issue.title} and copied ${result.inventory_copied} inventory item${result.inventory_copied === 1 ? '' : 's'}.`
      );
      setDuplicateSource(null);
      setDuplicateForm(emptyDuplicateForm);
      await loadIssues(filterPublicationId || undefined);
    } catch (e: any) {
      setDuplicateError(e?.response?.data?.message || 'Failed to duplicate issue.');
    } finally {
      setDuplicating(false);
    }
  };

  const visibleItems = items
    .filter((item) => statusFilter === 'all' || (item.status || 'active') === statusFilter)
    .sort((a, b) => {
      if (statusFilter === 'all' && (a.status || 'active') !== (b.status || 'active')) {
        return (a.status || 'active') === 'active' ? -1 : 1;
      }
      return String(b.issue_date || '').localeCompare(String(a.issue_date || ''));
    });

  return (
    <Layout>
      <MagazinePageHeader
        title="Issues"
        description="Manage issues and editions for each publication."
        action={canManageMagazineCatalog ? <MagazineNewButton onClick={openNewForm} /> : undefined}
      />

      <PageCard className="mb-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600">Filter by publication</label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={filterPublicationId}
              onChange={(e) => setFilterPublicationId(e.target.value)}
            >
              <option value="">All publications</option>
              {publications.map((pub) => (
                <option key={pub.id} value={pub.id}>{pub.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600">Issue status</label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="active">Active issues</option>
              <option value="inactive">Inactive issues</option>
              <option value="all">All statuses</option>
            </select>
          </div>
        </div>
      </PageCard>

      {successMessage && (
        <div className="mb-6 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {successMessage}
        </div>
      )}

      {canManageMagazineCatalog && (
        <MagazineFormModal
          open={showForm}
          title={editingId ? 'Edit issue' : 'New issue'}
          onClose={closeForm}
        >
          <form onSubmit={handleSubmit} className="grid min-w-0 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Publication</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.publication_id}
                onChange={(e) => setForm({ ...form, publication_id: e.target.value })}
                required
              >
                <option value="">Select publication</option>
                {publications.map((pub) => (
                  <option key={pub.id} value={pub.id}>{pub.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Title</label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Issue date</label>
              <input
                type="date"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Status</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <button
                type="submit"
                className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </MagazineFormModal>
      )}

      {canManageMagazineCatalog && (
        <MagazineFormModal
          open={Boolean(duplicateSource)}
          title="Duplicate issue"
          onClose={closeDuplicateForm}
        >
          <form onSubmit={handleDuplicate} className="grid min-w-0 gap-4 sm:grid-cols-2">
            <p className="text-sm text-gray-600 sm:col-span-2">
              Create a new issue from <span className="font-medium text-gray-900">{duplicateSource?.title}</span>.
              Inventory definitions will be copied without bookings or reserved quantities.
            </p>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Publication</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={duplicateForm.publication_id}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, publication_id: e.target.value })}
                disabled={duplicating}
                required
              >
                <option value="">Select publication</option>
                {publications.map((pub) => (
                  <option key={pub.id} value={pub.id}>{pub.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">New issue title</label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={duplicateForm.title}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, title: e.target.value })}
                disabled={duplicating}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Issue date</label>
              <input
                type="date"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={duplicateForm.issue_date}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, issue_date: e.target.value })}
                disabled={duplicating}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Status</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={duplicateForm.status}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, status: e.target.value })}
                disabled={duplicating}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
              <input
                type="checkbox"
                checked={duplicateForm.copy_inventory}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, copy_inventory: e.target.checked })}
                disabled={duplicating}
              />
              Copy inventory from the original issue
            </label>
            {duplicateError && <p className="text-sm text-red-600 sm:col-span-2">{duplicateError}</p>}
            <div className="flex gap-2 sm:col-span-2">
              <button
                type="submit"
                disabled={duplicating}
                className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
              >
                {duplicating ? 'Duplicating…' : 'Duplicate issue'}
              </button>
              <button
                type="button"
                onClick={closeDuplicateForm}
                disabled={duplicating}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </MagazineFormModal>
      )}

      <PageCard>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : visibleItems.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No {statusFilter === 'all' ? '' : `${statusFilter} `}issues found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Publication</th>
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Issue date</th>
                  <th className="py-2 pr-4">Status</th>
                  {(canManageMagazineCatalog || canViewMagazineReports) && <th className="py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 text-gray-900">{item.publication?.name || '—'}</td>
                    <td className="py-3 pr-4 font-medium text-gray-900">{item.title}</td>
                    <td className="py-3 pr-4 text-gray-600">{item.issue_date || '—'}</td>
                    <td className="py-3 pr-4 capitalize text-gray-600">{item.status}</td>
                    {(canManageMagazineCatalog || canViewMagazineReports) && (
                      <td className="py-3">
                        <div className="flex gap-2">
                          {canViewMagazineReports && (
                            <Link
                              to={`/magazine/reports?issue_id=${item.id}`}
                              className="text-sm text-torch-primary hover:underline"
                            >
                              Report
                            </Link>
                          )}
                          {canManageMagazineCatalog && (
                            <>
                              <button onClick={() => handleEdit(item)} className="text-sm text-torch-primary hover:underline">Edit</button>
                              <button onClick={() => openDuplicateForm(item)} className="text-sm text-torch-primary hover:underline">Duplicate</button>
                              <button onClick={() => handleDelete(item.id)} className="text-sm text-red-600 hover:underline">Delete</button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default Issues;
